import React, {useReducer} from 'react';
import {Breadcrumbs, Link, List, ListItem, ListItemButton, ListItemText, Stack, Typography} from '@mui/material';
import {Widget, WidgetTitle} from "../../Common";
import {JsonAnimal} from "../../../api/generated/rest-dto";
import {useSuspenseQuery} from "@tanstack/react-query";
import {PedigreeService} from "../../../services/pedigree-service";
import {ErrorBox, InfoBox} from "../../Common/alerts";
import {LoadingSuspense} from "../../Common/loading-suspense";
import {Merge, NavigateNext} from "@mui/icons-material";
import {RegistryIcon} from "../../../icons";
import {useTranslation} from "react-i18next";
import {AnimalService} from "../../../services/animal-service";
import {useAnimal} from "../../Common/hooks/use-animal";
import {SexIcon} from "../../../icons/SexIcon";

type OffspringWidgetProps = {
    animal: JsonAnimal
};

const useLineage = (ancestor: string) => {
    const [lineage, select] = useReducer((lineage: string[], panonId: string) => {
        const index = lineage.lastIndexOf(panonId);
        return index === -1 ? [...lineage, panonId] : lineage.slice(0, index + 1);
    }, [ancestor]);

    return {
        lineage,
        active: lineage[lineage.length - 1],
        select,
    }
};

const LineageElement = ({panonId, onClick}: { panonId: string, onClick: () => void }) => {
    const {animal} = useAnimal(panonId);

    return (
        <Link href="#" onClick={ev => { ev.preventDefault(); onClick(); }}>
            <Stack direction="row" alignItems="center">
                <SexIcon sex={animal?.sex ?? 'UNKNOWN'}/>
                {AnimalService.getAnimalFullName(animal)}
            </Stack>
        </Link>
    );
};

const Offspring = ({panonId, onSelect}: { panonId: string, onSelect: (panonId: string) => void }) => {
    const {t} = useTranslation();

    const {data: offspring} = useSuspenseQuery({
        queryKey: ['offspring', {panonId}],
        queryFn: () => PedigreeService.loadOffspringByPanonId(panonId),
    });

    if (!offspring.length) {
        return <InfoBox>Keine Nachkommen vorhanden.</InfoBox>;
    }

    return (
        <List>
            {offspring.map(o => (
                <ListItem key={o.panonId} disablePadding>
                    <ListItemButton onClick={() => onSelect(o.panonId)}>
                        <ListItemText secondary={o.registries.length > 0 && (
                            <Stack direction="row">
                                <RegistryIcon fontSize="small" titleAccess={t("registered_at")}/>
                                <Typography>{o.registries}</Typography>
                            </Stack>
                        )}>
                            {o.fullName} ({t('color.' + o.color)})
                        </ListItemText>
                    </ListItemButton>
                </ListItem>
            ))}
        </List>
    );
};

const OffspringWidget: React.FC<OffspringWidgetProps> = ({animal}) => {
    const initialPanonId = animal.panonIdentifier.id;

    const {lineage, active, select} = useLineage(initialPanonId);

    return (
        <Widget displayName={
            <WidgetTitle startIcon={<Merge color="success" />}>
                Nachkommen
            </WidgetTitle>
        } editable={false}>
            <Stack gap={1}>
                {initialPanonId !== active && (
                    <Breadcrumbs
                        separator={<NavigateNext fontSize="small"/>}
                        aria-label="breadcrumb"
                    >
                        {lineage.map(panonId => <LineageElement key={panonId}
                                                                panonId={panonId}
                                                                onClick={() => select(panonId)}/>)}
                    </Breadcrumbs>
                )}

                <LoadingSuspense error={<ErrorBox>Fehler beim Laden der Nachkommen.</ErrorBox>}>
                    <Offspring panonId={active} onSelect={select}/>
                </LoadingSuspense>
            </Stack>
        </Widget>
    );
};

export default OffspringWidget
